/**
 * ProductScoreGauge — SibaScore, a CIBIL-style 0-1000 trust rating.
 *
 * Renders a semi-circular arc gauge with:
 *   trusted   (800-1000) → emerald
 *   solid     (650-799)  → saffron (brand)
 *   emerging  (500-649)  → amber
 *   needs     (0-499)    → red
 *
 * Two size variants:
 *   size="sm"  → 60px badge for product cards / lists
 *   size="md"  → 180px hero for product detail / wizard review
 */
import React from "react";

export type ScoreBand = "trusted" | "solid" | "emerging" | "needs_improvement";

const MIN = 0;
const MAX = 1000;
const START_ANGLE = -180; // half-circle from left...
const SWEEP = 180;        // ...to right

const bandLabel: Record<ScoreBand, string> = {
  trusted: "Trusted",
  solid: "Solid",
  emerging: "Emerging",
  needs_improvement: "Needs improvement",
};

const bandColor: Record<ScoreBand, string> = {
  trusted: "#10B981",           // emerald
  solid: "#FF4F00",             // Baba Saffron
  emerging: "#F59E0B",          // amber
  needs_improvement: "#EF4444", // red
};

const bandFromScore = (s: number): ScoreBand => {
  if (s >= 800) return "trusted";
  if (s >= 650) return "solid";
  if (s >= 500) return "emerging";
  return "needs_improvement";
};

interface Props {
  score: number | null | undefined;
  band?: ScoreBand;
  size?: "sm" | "md";
  showLabel?: boolean;
  className?: string;
}

export const ProductScoreGauge: React.FC<Props> = ({
  score, band, size = "sm", showLabel = true, className = "",
}) => {
  const isEmpty = score == null;
  const s = isEmpty ? MIN : Math.max(MIN, Math.min(MAX, score!));
  const finalBand = band || bandFromScore(s);
  const color = bandColor[finalBand];

  // % filled along the arc (0..1)
  const filled = (s - MIN) / (MAX - MIN);

  const isSm = size === "sm";
  const dim = isSm ? 68 : 220;
  const stroke = isSm ? 6 : 14;
  const cx = dim / 2;
  const cy = isSm ? dim - 12 : dim - 40; // room for arc
  const r = (dim / 2) - (stroke / 2) - 2;

  // Arc math — draw a semi-circle from left (angle=180°) to right (0°)
  const arcPath = (fromFrac: number, toFrac: number) => {
    const rad = (deg: number) => (deg * Math.PI) / 180;
    const a1 = 180 - fromFrac * 180;
    const a2 = 180 - toFrac * 180;
    const x1 = cx + r * Math.cos(rad(a1));
    const y1 = cy - r * Math.sin(rad(a1));
    const x2 = cx + r * Math.cos(rad(a2));
    const y2 = cy - r * Math.sin(rad(a2));
    const largeArc = Math.abs(a1 - a2) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const svgHeight = isSm ? dim - 20 : dim - 60;

  return (
    <div
      className={`inline-flex flex-col items-center ${className}`}
      data-testid="product-score-gauge"
      data-score={score ?? "none"}
      data-band={isEmpty ? "unscored" : finalBand}
    >
      <div className="relative" style={{ width: dim, height: svgHeight + (isSm ? 0 : 8) }}>
        <svg
          width={dim}
          height={svgHeight}
          viewBox={`0 0 ${dim} ${svgHeight}`}
          className="overflow-visible"
        >
          {/* Track */}
          <path
            d={arcPath(0, 1)}
            fill="none"
            stroke="rgba(15, 23, 42, 0.08)"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          {/* Filled */}
          <path
            d={arcPath(0, isEmpty ? 0.001 : filled)}
            fill="none"
            stroke={isEmpty ? "rgba(15, 23, 42, 0.16)" : color}
            strokeWidth={stroke}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 480ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          />
        </svg>
        {/* Overlayed score text (positioned absolutely to guarantee no clipping) */}
        <div
          className="absolute inset-x-0 flex flex-col items-center pointer-events-none"
          style={{ top: isSm ? "35%" : "35%" }}
        >
          <span
            className="font-display leading-none"
            style={{
              fontSize: isSm ? 20 : 48,
              fontWeight: 800,
              letterSpacing: -0.5,
              color: "var(--sg-fg, #0F172A)",
            }}
          >
            {isEmpty ? "—" : s}
          </span>
          {!isSm && (
            <span
              className="mt-1"
              style={{
                fontSize: 10, textTransform: "uppercase", letterSpacing: 1.4, fontWeight: 700,
                color: "var(--sg-muted, #64748B)",
              }}
            >
              SIBA Score
            </span>
          )}
        </div>
      </div>
      {showLabel && (
        <div
          className={`mt-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isSm ? "" : "text-xs px-3 py-1"}`}
          style={{ backgroundColor: `${color}1A`, color: color }}
        >
          {isEmpty ? "Unscored" : bandLabel[finalBand]}
        </div>
      )}
    </div>
  );
};

export default ProductScoreGauge;
